import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface NavigationGuardContextType {
    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;
    requestNavigation: (path: string) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextType>({
    isDirty: false,
    setIsDirty: () => { },
    requestNavigation: () => { },
});

interface NavigationGuardProviderProps {
    children: React.ReactNode;
    renderModal: (isOpen: boolean, onConfirm: () => Promise<any>, onCancel: () => Promise<any>) => React.ReactNode;
}

export const NavigationGuardProvider = ({ children, renderModal }: NavigationGuardProviderProps) => {
    const [isDirty, setIsDirty] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    // Browser-level guard (refresh, tab close, back/forward)
    useEffect(() => {
        if (!isDirty) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const requestNavigation = useCallback((path: string) => {
        if (isDirty) {
            setPendingPath(path);
            setShowModal(true);
        } else {
            navigate(path);
        }
    }, [isDirty, navigate]);

    const confirmNavigation = useCallback(async () => {
        setIsDirty(false);
        setShowModal(false);
        if (pendingPath) {
            navigate(pendingPath);
            setPendingPath(null);
        }
    }, [pendingPath, navigate]);

    const cancelNavigation = useCallback(async () => {
        setShowModal(false);
        setPendingPath(null);
    }, []);

    return (
        <NavigationGuardContext.Provider value={{ isDirty, setIsDirty, requestNavigation }}>
            {children}
            {renderModal(showModal, confirmNavigation, cancelNavigation)}
        </NavigationGuardContext.Provider>
    );
};

export const useNavigationGuard = () => useContext(NavigationGuardContext);